import type { ChordBorrowing, ChordFunction } from './types.js'

/**
 * Map an analyzer-emitted ChordFunction to its `function-*` lexicon id, for
 * the 14 diatonic functions covered by the Phase 1.5 starter set. All other
 * ChordFunction values resolve to null — honest "no lexicon coverage yet"
 * signal that lets consumers fall back to the legacy `description` field.
 *
 * The two-character casing distinction (`I` major vs `i` minor) is the
 * canonical disambiguator — don't normalize.
 *
 * Lives in `analysis/` (not `lexicon/`) because it interprets analyzer
 * output. The lexicon stays free of analyzer-knowledge.
 */
const FUNCTION_LEXICON_MAP: Partial<Record<ChordFunction, string>> = {
  // major-key diatonic
  I: 'function-I',
  ii: 'function-ii',
  iii: 'function-iii',
  IV: 'function-IV',
  V: 'function-V',
  vi: 'function-vi',
  'vii°': 'function-vii-dim',
  // minor-key diatonic
  i: 'function-i',
  'ii°': 'function-ii-dim',
  '♭III': 'function-flat-III',
  iv: 'function-iv',
  v: 'function-v',
  '♭VI': 'function-flat-VI',
  '♭VII': 'function-flat-VII',
}

/**
 * Parallel-mode borrowing → `borrowed-*` lexicon id. Keys on the analyzer's
 * borrowed-chord ChordFunction labels (which match the parallel-minor
 * diatonic labels). Mirrors `MAJOR_BORROWING_MAP` in `parallel-mode-name.ts`
 * — kept here so this module is self-contained and can be unit-tested
 * without standing up the analyzer.
 */
const BORROWED_LEXICON_MAP: Partial<Record<ChordFunction, string>> = {
  '♭VII': 'borrowed-flat-VII-major',
  iv: 'borrowed-iv-major',
  '♭III': 'borrowed-flat-III-major',
  '♭VI': 'borrowed-flat-VI-major',
  '♭II': 'borrowed-flat-II-major',
}

/**
 * Resolve the lexicon id that gives user-facing copy for a chord-analyzer
 * verdict. Dispatch:
 *   - `borrowing === 'parallel-mode'` → `borrowed-*` id (or null if outside
 *     Phase-1 coverage — e.g., minor-key borrowings)
 *   - `borrowing === 'secondary-dominant'` → null (no function-* coverage
 *     for V/X labels in this PR)
 *   - `borrowing === null` → `function-*` id (or null for chromatic, modal-
 *     distinctive chords, tritone subs, harmonic-minor extensions, etc.)
 */
export const getFunctionLexiconId = (chordFunction: ChordFunction, borrowing: ChordBorrowing): string | null => {
  if (borrowing === 'parallel-mode') {
    return BORROWED_LEXICON_MAP[chordFunction] ?? null
  }
  if (borrowing === 'secondary-dominant') {
    return null
  }
  return FUNCTION_LEXICON_MAP[chordFunction] ?? null
}
